import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  BusinessOutlined as BusinessIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import { NavPanel, PanelGroup, PanelButton } from '../../components/layout/NavPanel';

interface Company {
  _id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  status: string;
  createdAt?: string;
}

const CompanyList: React.FC = () => {
  const { account } = useAuthStore();
  const isSystemAdmin = account?.role === 'SA';
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  const [newCompany, setNewCompany] = useState({
    name: '',
    address: '',
    initialUser: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
    },
  });

  const [editCompany, setEditCompany] = useState({
    name: '',
    address: '',
  });

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data: any = await apiClient.get('/api/companies');
      setCompanies(data.companies || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAddCompany = async () => {
    if (!newCompany.name || !newCompany.address) {
      setError('Company fields (name, address) are required');
      return;
    }
    
    if (!newCompany.initialUser.firstName || !newCompany.initialUser.lastName || 
        !newCompany.initialUser.email || !newCompany.initialUser.phone) {
      setError('Initial Account Owner fields (first name, last name, email, phone) are required');
      return;
    }
    
    // Password is optional - if not provided, user will receive invitation email

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.post('/api/companies', newCompany);
      setSuccess('Company created successfully!');
      setAddDialogOpen(false);
      setNewCompany({
        name: '',
        address: '',
        initialUser: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
        },
      });
      fetchCompanies();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create company');
      console.error('Error creating company:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCompany = async () => {
    if (!companyToEdit || !editCompany.name || !editCompany.address) {
      setError('All fields are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.put(`/api/companies/${companyToEdit._id}`, editCompany);
      setSuccess('Company updated successfully!');
      setEditDialogOpen(false);
      setCompanyToEdit(null);
      fetchCompanies();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update company');
      console.error('Error updating company:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.delete(`/api/companies/${companyToDelete._id}`);
      setSuccess(`Company ${companyToDelete.name} deleted successfully!`);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      fetchCompanies();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete company');
      console.error('Error deleting company:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (company: Company) => {
    setCompanyToEdit(company);
    setEditCompany({
      name: company.name,
      address: company.address,
    });
    setEditDialogOpen(true);
  };

  const visible = companies.filter((c) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [c.name, c.address, c.email, c.phone]
      .some((v) => (v || '').toLowerCase().includes(term));
  });

  const activeCount = companies.filter((c) => c.status === 'active').length;

  const columns: Column<Company>[] = [
    {
      key: 'name',
      header: 'Company',
      render: (c) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
          <Typography variant="caption" color="text.secondary">{c.address || 'No address on file'}</Typography>
        </Box>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (c) => <Typography variant="body2" color="text.secondary">{c.email || '—'}</Typography>,
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (c) => (
        <Typography variant="body2" color="text.secondary">
          {formatPhoneNumber(c.phone) || '—'}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => (
        <ToneChip tone={c.status === 'active' ? 'ok' : 'neutral'} label={c.status} />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 110,
      render: (c) => (
        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title="Edit company">
            <IconButton size="small" onClick={() => openEditDialog(c)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete company">
            <IconButton
              size="small"
              onClick={() => { setCompanyToDelete(c); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <NavPanel>
        {isSystemAdmin && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ mb: 2.5, py: 1 }}
          >
            Add a company
          </Button>
        )}

        <PanelGroup label="Tenants">
          <PanelButton
            icon={<BusinessIcon />}
            label="All companies"
            count={companies.length}
            active
            onClick={() => setSearch('')}
          />
        </PanelGroup>
      </NavPanel>

      <PageBody>
        <PageHeader
          title="Companies"
          subtitle={`${companies.length} total · ${activeCount} active`}
        >
          <TextField
            placeholder="Find a company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 420, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </PageHeader>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <DataTable
          columns={columns}
          rows={visible}
          rowKey={(c) => c._id}
          loading={loading}
          empty={
            <EmptyState
              compact
              icon={<BusinessIcon />}
              title={search ? 'No company matches that' : 'No companies yet'}
              description={
                search
                  ? 'Try a different name, address or email.'
                  : 'Every tenant on this deployment will be listed here.'
              }
              action={
                search
                  ? { label: 'Clear search', onClick: () => setSearch('') }
                  : isSystemAdmin
                    ? { label: 'Add a company', onClick: () => setAddDialogOpen(true), icon: <AddIcon /> }
                    : undefined
              }
            />
          }
        />
      </PageBody>

      {/* Add Company Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Company</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Company Name"
              value={newCompany.name}
              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Address"
              value={newCompany.address}
              onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
              fullWidth
              required
              multiline
              rows={2}
            />
            
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
              Initial Account Owner (AO)
            </Typography>
            
            <TextField
              label="First Name"
              value={newCompany.initialUser.firstName}
              onChange={(e) => setNewCompany({ 
                ...newCompany, 
                initialUser: { ...newCompany.initialUser, firstName: e.target.value }
              })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={newCompany.initialUser.lastName}
              onChange={(e) => setNewCompany({ 
                ...newCompany, 
                initialUser: { ...newCompany.initialUser, lastName: e.target.value }
              })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={newCompany.initialUser.email}
              onChange={(e) => setNewCompany({ 
                ...newCompany, 
                initialUser: { ...newCompany.initialUser, email: e.target.value }
              })}
              fullWidth
              required
              helperText="If user exists, they will be invited to this company"
            />
            <TextField
              label="Phone"
              value={newCompany.initialUser.phone}
              onChange={(e) => setNewCompany({ 
                ...newCompany, 
                initialUser: { ...newCompany.initialUser, phone: e.target.value }
              })}
              fullWidth
              required
            />
            <TextField
              label="Password (Optional)"
              type="password"
              value={newCompany.initialUser.password}
              onChange={(e) => setNewCompany({ 
                ...newCompany, 
                initialUser: { ...newCompany.initialUser, password: e.target.value }
              })}
              fullWidth
              helperText="If not provided, user will receive an invitation email to set password"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddCompany}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Company Name"
              value={editCompany.name}
              onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Address"
              value={editCompany.address}
              onChange={(e) => setEditCompany({ ...editCompany, address: e.target.value })}
              fullWidth
              required
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleEditCompany}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Update Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Company</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{companyToDelete?.name}</strong>?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. All users and data associated with this company will be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCompany}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyList;
